import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { addAsyncState } from '@repo/store';
import type { AccountSubscription } from '@repo/utils/src/subscription/types';
import { mapToObject } from '@repo/utils/src/utils';

import { getDashboardAccount } from '@/dal/getDashboardAccount';
import { getDashboardConfig } from '@/dal/getDashboardConfig';
import { logout, logoutUser } from '@/store/auth';
import { createAppAsyncThunk } from '@/store/createAppAsyncThunk';
import { IAccount, IPayoutOnboardingStatus, ISubscription, IVotePage } from '@/types/models';
import { ApiResponse } from '@/types/types';
import api from '@/utils/api';
import { getVotePagesEndpoint } from '@/utils/endpoints';

import { DashboardState } from './types';

export const dashboardSliceName = 'dashboard';

const INITIAL_STATE: DashboardState = {
  initialized: false,
  votePagesFetched: false,
  initializingDashboard: false,
  initializeFailed: false,
  configLoaded: false,
  account: null,
  accountSubscription: null,
  trialEligible: false,
  votePages: {},
  config: {
    templates: [],
    vote_types: {},
    subscription_types: { free: {} as ISubscription, pro: {} as ISubscription, premium: {} as ISubscription },
    currencies: [],
    stripe_publishable_key: '',
    flutterwave_public_key: '',
    google_client_id: '',
  },
  payoutStatus: null,
};

const CONFIG_MAX_TRIES = 3;
const CONFIG_RETRY_DELAY_MS = 1000;

export const fetchDashboardConfig = createAppAsyncThunk(
  `${dashboardSliceName}/fetchDashboardConfig`,
  async () => {
    for (let attempt = 1; ; attempt++) {
      try {
        return await getDashboardConfig();
      } catch (e) {
        if (attempt === CONFIG_MAX_TRIES) {
          console.error('failed to fetch dashboard config after retries', e);
          throw e;
        }
        await new Promise((resolve) => setTimeout(resolve, CONFIG_RETRY_DELAY_MS));
      }
    }
  },
  { condition: (_, { getState }) => !getState()[dashboardSliceName].configLoaded }
);

export const fetchVotePages = createAppAsyncThunk(`${dashboardSliceName}/fetchVotePages`, async (accountId: string) => {
  try {
    const { data, status } = (await api.request<ApiResponse>(getVotePagesEndpoint(accountId))).data;
    if (status !== 1) throw new Error('Could not fetch vote pages');

    return data.vote_pages as IVotePage[];
  } catch (e) {
    console.error('failed to get vote pages', e);
    throw e;
  }
});

/** The signed-in account, its config and vote pages. A failure logs out, since nothing works without them. */
export const initDashboard = createAppAsyncThunk(
  `${dashboardSliceName}/initDashboard`,
  async (_: void, { dispatch, rejectWithValue }) => {
    try {
      await dispatch(fetchDashboardConfig());
      const { account, accountSubscription, trialEligible } = await getDashboardAccount();
      dispatch(setDashboardSettings({ account, accountSubscription, trialEligible }));
      dispatch(fetchVotePages(account.id as string));
    } catch (e) {
      console.error('dashboard initialization failed', e);
      dispatch(logout());
      return rejectWithValue('Could not load your account');
    }
  },
  { condition: (_, { getState }) => !getState()[dashboardSliceName].initializingDashboard }
);

export const dashboardSlice = createSlice({
  name: dashboardSliceName,
  initialState: INITIAL_STATE satisfies DashboardState as DashboardState,
  reducers: {
    setDashboardSettings(
      state,
      {
        payload,
      }: PayloadAction<{ account: IAccount; accountSubscription: AccountSubscription | null; trialEligible: boolean }>
    ) {
      state.account = payload.account;
      state.accountSubscription = payload.accountSubscription;
      state.trialEligible = payload.trialEligible;
      state.initialized = true;
    },
    setTrialEligible(state, { payload }: PayloadAction<boolean>) {
      state.trialEligible = payload;
    },
    addVotePage(state, { payload: { votePage } }: PayloadAction<{ votePage: IVotePage }>) {
      state.votePages[votePage.id] = votePage;
    },
    updateVotePage(state, { payload }: PayloadAction<{ votePageId: string; votePageUpdate: Partial<IVotePage> }>) {
      const { votePageId, votePageUpdate } = payload;
      state.votePages[votePageId] = { ...state.votePages[votePageId], ...votePageUpdate };
    },
    removeVotePage(state, { payload: votePageId }: PayloadAction<string>) {
      delete state.votePages[votePageId];
    },
    updateAccount(state, { payload }: PayloadAction<{ accountValues: Partial<IAccount> }>) {
      state.account = { ...(state.account as IAccount), ...payload.accountValues };
    },
    updateAccountSubscription(state, { payload: accountSubscription }: PayloadAction<Partial<AccountSubscription>>) {
      state.accountSubscription = { ...(state.accountSubscription as AccountSubscription), ...accountSubscription };
    },
    setPayoutStatus(state, { payload }: PayloadAction<IPayoutOnboardingStatus>) {
      state.payoutStatus = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardConfig.fulfilled, (state, { payload }) => {
        state.config = payload;
        state.configLoaded = true;
      })
      .addCase(fetchVotePages.fulfilled, (state, { payload }) => {
        state.votePages = mapToObject(payload);
        state.votePagesFetched = true;
      })
      .addCase(fetchVotePages.rejected, (state) => {
        state.votePages = {};
        state.votePagesFetched = false;
      })
      // Whoever ended the session, the next account must not see this one's.
      .addCase(logoutUser, (state) => {
        state.initialized = false;
        state.account = null;
        state.accountSubscription = null;
        state.trialEligible = false;
        state.payoutStatus = null;
      });
    addAsyncState(builder, initDashboard, (state, { loading, error }) => {
      state.initializingDashboard = loading;
      state.initializeFailed = error !== null;
    });
  },
});

export const {
  setDashboardSettings,
  setTrialEligible,
  addVotePage,
  updateVotePage,
  removeVotePage,
  updateAccount,
  updateAccountSubscription,
  setPayoutStatus,
} = dashboardSlice.actions;
